import { isValidIdentifier } from '@/lib/identifierValidation'
import type { OutputResolutionEntry, OutputResolutionMap } from '@/types/bundle'

const SYNTHETIC_KEY_PREFIX = '__new_'

// Mirrors document-types/fieldUtils.ts's synthetic-key pattern exactly:
// output_resolution's map key *is* the output field name itself (no
// separate response_tag-style property to key off), so a brand-new/
// duplicated entry gets a client-only placeholder key until the operator
// types the real name - resolveOutputResolutionForSave remaps it before Save.
export function createSyntheticOutputKey(): string {
  return `${SYNTHETIC_KEY_PREFIX}${crypto.randomUUID()}`
}

export function isSyntheticOutputKey(key: string): boolean {
  return key.startsWith(SYNTHETIC_KEY_PREFIX)
}

export function createBlankOutputEntry(): OutputResolutionEntry {
  return { priority: [], min_confidence: 0 }
}

export function duplicateOutputEntry(source: OutputResolutionEntry): OutputResolutionEntry {
  return structuredClone(source)
}

export interface ResolvedOutputResolutionForSave {
  resolved: OutputResolutionMap
  duplicateKeys: string[]
  emptyKeys: string[]
  invalidKeys: string[]
}

// Remaps every synthetic (unsaved new/duplicated) entry key to the field
// name typed into its row. Never silently overwrites - an empty, invalid-
// shaped, or colliding name is reported instead of resolved, so the caller
// can block Save on it (two rows resolving to the same key is a real
// JSON-structural impossibility, not just a UX nicety - one would silently
// clobber the other; an invalid-shaped key would be rejected server-side
// anyway, per PRD 34 - this only makes that visible before Save rather than
// after).
export function resolveOutputResolutionForSave(
  entries: OutputResolutionMap,
  keyDrafts: Record<string, string>,
): ResolvedOutputResolutionForSave {
  const resolved: OutputResolutionMap = {}
  const emptyKeys: string[] = []
  const duplicateKeys: string[] = []
  const invalidKeys: string[] = []

  for (const [key, entry] of Object.entries(entries)) {
    const effectiveKey = isSyntheticOutputKey(key) ? (keyDrafts[key] ?? '').trim() : key

    if (effectiveKey.length === 0) {
      emptyKeys.push(key)
      continue
    }
    if (!isValidIdentifier(effectiveKey)) {
      invalidKeys.push(key)
      continue
    }
    if (effectiveKey in resolved) {
      duplicateKeys.push(key)
      continue
    }
    resolved[effectiveKey] = entry
  }

  return { resolved, duplicateKeys, emptyKeys, invalidKeys }
}
